import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {useApiQuery} from "../api/hooks";
import apiClient from "../api/apiClient";
import {usePreloaderStore} from "../stores";

/**
 * Централизованный хук для управления состоянием редактирования Dozor.
 * Содержит все данные и методы для работы с точками, переходами, командами и последовательностями.
 */
export default function useDozorEditState(dozorId) {
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();

    // ============ Query Keys ============
    const queryKeys = {
        points: ['dozor', 'points', dozorId],
        transitions: ['dozor', 'transitions', dozorId],
        teams: ['dozor', 'teams', dozorId],
        sequences: ['dozor', 'sequences', dozorId],
    };

    // ============ Queries ============
    const {data: pointsData, isLoading: pointsLoading} = useApiQuery(
        queryKeys.points,
        `/api/dozor/admin/points/list-by-game?dozorId=${dozorId}`,
        {refetchOnMount: 'always', enabled: !!dozorId}
    );

    const {data: transitionsData, isLoading: transitionsLoading} = useApiQuery(
        queryKeys.transitions,
        `/api/dozor/admin/transitions/list-by-game?dozorId=${dozorId}`,
        {refetchOnMount: 'always', enabled: !!dozorId}
    );

    const {data: teamsData, isLoading: teamsLoading} = useApiQuery(
        queryKeys.teams,
        `/api/dozor/admin/teams/list?dozorId=${dozorId}`,
        {refetchOnMount: 'always', enabled: !!dozorId}
    );

    const {data: sequencesData, isLoading: sequencesLoading} = useApiQuery(
        queryKeys.sequences,
        `/api/dozor/admin/sequences/find-all?dozorId=${dozorId}`,
        {refetchOnMount: 'always', enabled: !!dozorId}
    );

    // ============ Sorted Data ============
    const points = useMemo(() =>
        [...(pointsData || [])].sort((a, b) => a.orderId - b.orderId),
        [pointsData]
    );

    const transitions = useMemo(() =>
        [...(transitionsData || [])].sort((a, b) => a.orderId - b.orderId),
        [transitionsData]
    );

    const teams = useMemo(() => teamsData || [], [teamsData]);
    const sequences = useMemo(() => sequencesData || [], [sequencesData]);

    // ============ Invalidation Methods ============
    const invalidatePoints = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.points});
    };

    const invalidateTransitions = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.transitions});
    };

    const invalidateTeams = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.teams});
    };

    const invalidateSequences = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.sequences});
    };

    const invalidateTeamsAndSequences = () => {
        invalidateTeams();
        invalidateSequences();
    };

    // ============ Points Actions ============
    const createPoint = async () => {
        showPreloader();
        try {
            const nextOrderId = (pointsData?.length || 0) + 1;
            await apiClient.post('/api/dozor/admin/points/create', {
                dozorId,
                orderId: nextOrderId,
                title: `Точка ${nextOrderId}`,
                description: "",
                textHint: "",
                incorrectCodeCost: 5,
                time: "00:10:00"
            });
            invalidatePoints();
            enqueueSnackbar("Точка создана", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при создании точки", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    const savePoint = async (pointId, pointData, pendingFiles = []) => {
        showPreloader();
        try {
            const formData = new FormData();
            formData.append('point', JSON.stringify({
                orderId: pointData.orderId,
                title: pointData.title,
                description: pointData.description,
                textHint: pointData.textHint,
                incorrectCodeCost: pointData.incorrectCodeCost,
                time: pointData.time,
                mediaList: pointData.mediaList
            }));

            for (const file of pendingFiles) {
                formData.append('newFiles', file);
            }

            const response = await apiClient.put(
                `/api/dozor/admin/points/save/${pointId}`,
                formData,
                {headers: {'Content-Type': 'multipart/form-data'}}
            );

            invalidatePoints();
            enqueueSnackbar("Сохранено", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deletePoint = async (pointId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/dozor/admin/points/delete/${pointId}`);
            invalidatePoints();
            enqueueSnackbar("Точка удалена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении точки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const loadPointDetails = async (pointId) => {
        const response = await apiClient.get(`/api/dozor/admin/points/find-by-id/${pointId}`);
        return response.data;
    };

    // ============ Transitions Actions ============
    const createTransition = async () => {
        showPreloader();
        try {
            await apiClient.post(`/api/dozor/admin/transitions/create?dozorId=${dozorId}`);
            invalidateTransitions();
            enqueueSnackbar("Переход создан", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при создании перехода", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    const saveTransition = async (transitionId, transitionData, pendingFiles = []) => {
        showPreloader();
        try {
            const formData = new FormData();
            formData.append('transition', JSON.stringify({
                orderId: transitionData.orderId,
                title: transitionData.title,
                description: transitionData.description,
                textHint: transitionData.textHint,
                time: transitionData.time,
                fromPointId: transitionData.fromPointId,
                toPointId: transitionData.toPointId,
                mediaList: transitionData.mediaList
            }));

            for (const file of pendingFiles) {
                formData.append('newFiles', file);
            }

            const response = await apiClient.put(
                `/api/dozor/admin/transitions/save/${transitionId}`,
                formData,
                {headers: {'Content-Type': 'multipart/form-data'}}
            );

            invalidateTransitions();
            enqueueSnackbar("Сохранено", {variant: "success"});
            return response.data;
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении перехода", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteTransition = async (transitionId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/dozor/admin/transitions/delete/${transitionId}`);
            invalidateTransitions();
            enqueueSnackbar("Переход удалён", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении перехода", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const loadTransitionDetails = async (transitionId) => {
        const response = await apiClient.get(`/api/dozor/admin/transitions/find-by-id/${transitionId}`);
        return response.data;
    };

    // ============ Teams Actions ============
    const refreshTeams = async () => {
        showPreloader();
        try {
            const response = await apiClient.post(`/api/dozor/admin/teams/refresh?dozorId=${dozorId}`);
            invalidateTeamsAndSequences();
            enqueueSnackbar(response.data.message || "Команды обновлены", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении команд", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    const deleteTeam = async (teamStateId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/dozor/admin/teams/delete?stateId=${teamStateId}`);
            invalidateTeamsAndSequences();
            enqueueSnackbar("Команда удалена", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении команды", {variant: "error"});
        } finally {
            hidePreloader();
        }
    };

    // ============ Sequences Actions ============
    const addSequence = async (teamStateId, entityType, entityId) => {
        showPreloader();
        try {
            await apiClient.post('/api/dozor/admin/sequences/new', {
                dozorId,
                teamStateId,
                gameEntityType: entityType,
                entityId
            });
            invalidateSequences();
            enqueueSnackbar("Добавлено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar(error.response?.data?.error || "Ошибка при добавлении", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteSequence = async (sequenceId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/dozor/admin/sequences/delete/${sequenceId}`);
            invalidateSequences();
            enqueueSnackbar("Удалено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const saveSequence = async (sequenceId, sequenceData) => {
        showPreloader();
        try {
            await apiClient.put(`/api/dozor/admin/sequences/update/${sequenceId}`, sequenceData);
            invalidateSequences();
            enqueueSnackbar("Сохранено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    // ============ Helper: Get sequences for team ============
    const getTeamSequences = (teamStateId) => {
        return sequences.filter(s => s.teamStateId === teamStateId);
    };

    return {
        // Data
        points,
        transitions,
        teams,
        sequences,

        // Loading states
        isLoading: pointsLoading || transitionsLoading || teamsLoading || sequencesLoading,
        pointsLoading,
        transitionsLoading,
        teamsLoading,
        sequencesLoading,

        // Points actions
        createPoint,
        savePoint,
        deletePoint,
        loadPointDetails,

        // Transitions actions
        createTransition,
        saveTransition,
        deleteTransition,
        loadTransitionDetails,

        // Teams actions
        refreshTeams,
        deleteTeam,

        // Sequences actions
        addSequence,
        deleteSequence,
        saveSequence,
        getTeamSequences,

        // Invalidation (для внешнего использования если нужно)
        invalidatePoints,
        invalidateTransitions,
        invalidateTeams,
        invalidateSequences,
        invalidateTeamsAndSequences,
    };
}
